import React, { memo, useCallback, useEffect } from 'react';
import { StyleSheet } from 'react-native';
import Animated, {
    FadeInRight,
    SharedValue,
    useAnimatedReaction,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';

import { PlayerProjectionFragment } from '@/api/events/query.generated';
import ChevronRight from '@/assets/icons/chevronRight';
import InfoIcon from '@/assets/icons/info-small';
import LiveIcon from '@/assets/icons/live-indicator';
import LockIcon from '@/assets/icons/lock';
import { AnimatedNumber } from '@/components/AnimatedNumber';
import { Text } from '@/components/TextComponent';
import { Box, Row, TouchableBox } from '@/components/lib/components';
import { useFantasyPoints } from '@/feature/betslip-pickem/components/FantasyPointsModalProvider';
import { BetslipPick } from '@/feature/betslip-pickem/types';
import { SingleProjectionIcon } from '@/feature/lobby/components/SingleProjectionIcon';
import { checkIfRegularProjection } from '@/feature/lobby/utils/lineupsUtils';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { designSystem } from '@/styles/styles';
import { League, MarketOption, MarketStatus, Sport } from '@/types/api.generated';
import { logger } from '@/utils/logging';

import { PickButtons } from '../PickButtons';

const AnimatedBox = Animated.createAnimatedComponent(Box);

export const AlternateProjectionRowContent = memo(
    ({
        league,
        sport,
        selectMarketOption,
        removePlayer,
        expanded,
        expandedIndex,
        hideExpandButton,
        rowIndex,
        projectionsGroup,
        activeAlternateIndex,
        playerPicked,
    }: {
        league: League;
        sport: Sport;
        selectMarketOption: (projection: PlayerProjectionFragment, option: MarketOption) => void;
        removePlayer: () => void;
        expanded: SharedValue<boolean>;
        expandedIndex: SharedValue<number>;
        rowIndex: number;
        hideExpandButton: boolean;
        projectionsGroup: PlayerProjectionFragment[];
        activeAlternateIndex: number;
        playerPicked?: BetslipPick;
    }) => {
        const chevronAnim = useSharedValue(0);
        const isSuspended = projectionsGroup.every(projection => projection.marketStatus === MarketStatus.Suspended);
        const enableInPlayCurrentValue = useJurisdictionStore(
            store => store.jurisdictionSettings?.productConfig?.settings?.enable_inplay_current_value
        );

        useEffect(() => {
            if (isSuspended && expanded.value) {
                expandedIndex.value = -1;
            }
        }, [expanded, expandedIndex, isSuspended]);

        useAnimatedReaction(
            () => expanded.value,
            expandedState => {
                chevronAnim.value = withTiming(expandedState ? 180 : 0, { duration: 250 });
            }
        );
        const chevronAnimStyle = useAnimatedStyle(() => {
            return {
                transform: [
                    {
                        rotate: `${chevronAnim.value}deg`,
                    },
                ],
            };
        });
        const groupLabel = projectionsGroup[0].label;
        const groupDisplayProjection = projectionsGroup[activeAlternateIndex];
        const isDisplayProjectionRegular = checkIfRegularProjection(groupDisplayProjection?.type);

        const hasCurrentValue = !!groupDisplayProjection?.currentValue;
        const shouldShowCurrentValue = enableInPlayCurrentValue && hasCurrentValue;
        const { showFantasyPointsModal } = useFantasyPoints();
        const isFantasyPtsText = (input: string) => input === 'FANTASY_POINTS';
        const handleFantasyPtsPress = (pickName: string) => {
            if (isFantasyPtsText(pickName)) {
                showFantasyPointsModal({ league, sport });
            }
        };

        const handleRowPress = useCallback(() => {
            expandedIndex.value = expandedIndex.value === rowIndex ? -1 : rowIndex;
        }, [expandedIndex, rowIndex]);

        const onPickButtonPress = useCallback(() => {
            expandedIndex.value = -1;
        }, [expandedIndex]);

        const singleProjection = projectionsGroup.length === 1;
        return (
            <Row px={'s16'} py={'s12'} justifyContent={'space-between'}>
                <TouchableBox
                    alignItems={'center'}
                    disabled={singleProjection || isSuspended}
                    onPress={handleRowPress}
                    flexDirection={'row'}
                    zIndex={1}
                    activeOpacity={1}
                >
                    {hideExpandButton ? null : (
                        <AnimatedBox style={chevronAnimStyle} mr={'s8'}>
                            <Box style={styles.chevron}>
                                <ChevronRight
                                    color={
                                        singleProjection || isSuspended
                                            ? designSystem.colors.transparent
                                            : designSystem.colors.white
                                    }
                                />
                            </Box>
                        </AnimatedBox>
                    )}
                    <Box>
                        <Row gap={'s6'} alignItems={'center'}>
                            <AnimatedNumber
                                alignText={'flex-start'}
                                decimals={1}
                                value={
                                    (isDisplayProjectionRegular
                                        ? groupDisplayProjection.value
                                        : groupDisplayProjection.nonRegularValue) ?? 0
                                }
                                color={isSuspended ? 'gray4' : 'white'}
                                variant={'titleLarge'}
                                letterSpacing={-0.43}
                            />

                            {!isDisplayProjectionRegular ? (
                                // the entering animation is fixing a jump in the UI caused by the animated number
                                // when it's going from a wider value to a smaller one
                                <Animated.View entering={FadeInRight.duration(200)}>
                                    <SingleProjectionIcon selectedProjection={groupDisplayProjection} />
                                </Animated.View>
                            ) : null}

                            {shouldShowCurrentValue ? (
                                <Row gap={'s6'}>
                                    <LiveIcon />
                                    <Text variant={'labelMedium'} color={'red500'}>
                                        {groupDisplayProjection.currentValue}
                                    </Text>
                                </Row>
                            ) : null}
                        </Row>

                        <Row alignItems={'center'}>
                            <Text variant={'bodyMedium'} color={isSuspended ? 'gray4' : 'gray3'}>
                                {groupLabel}
                            </Text>
                            {isFantasyPtsText(projectionsGroup[0].key) ? (
                                <TouchableBox ml={'s4'} onPress={() => handleFantasyPtsPress(projectionsGroup[0].key)}>
                                    <InfoIcon />
                                </TouchableBox>
                            ) : null}
                        </Row>
                    </Box>
                </TouchableBox>
                <TouchableBox disabled={singleProjection || isSuspended} flex={1} onPress={handleRowPress} />
                <Box ml={'s16'} justifyContent={'center'}>
                    {isSuspended ? (
                        <Row justifyContent={'center'} alignItems={'center'}>
                            <Text color={'gray4'}>Suspended</Text>
                            <Box p={'s12'}>
                                <LockIcon color={designSystem.colors.gray4} />
                            </Box>
                        </Row>
                    ) : (
                        <PickButtons
                            removeSelection={() => {
                                onPickButtonPress();
                                removePlayer();
                            }}
                            makeSelection={outcome => {
                                const marketOption = groupDisplayProjection.allowedOptions?.find(
                                    it => it.outcome === outcome
                                );
                                if (!marketOption) {
                                    logger.warn('No market option found for outcome', outcome);
                                    return;
                                }
                                const marketId = groupDisplayProjection.marketId;
                                if (!marketId) {
                                    logger.warn('No market id found for projection', groupDisplayProjection.label);
                                    return;
                                }
                                onPickButtonPress();
                                selectMarketOption(groupDisplayProjection, marketOption);
                            }}
                            projection={groupDisplayProjection}
                            playerPicked={playerPicked}
                        />
                    )}
                </Box>
            </Row>
        );
    }
);

const styles = StyleSheet.create({
    chevron: {
        transform: [{ rotate: '90deg' }],
    },
});
